import { useEffect, useRef, useState } from "react";
import { MessagesSquare, Send, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { api } from "../lib/api";
import ConfirmDialog from "../components/ConfirmDialog";

const ROLE_TR = { admin: "Yönetici", supplier: "Tedarikçi", buyer: "Mağaza" };
const ROLE_CLS = {
    admin: "bg-terra-600/15 text-terra-600",
    supplier: "bg-sage-600/15 text-sage-700",
    buyer: "bg-stone-500/15 text-stone-500",
};

const fmtTime = (iso) => {
    try {
        return new Date(iso).toLocaleTimeString("tr-TR", { hour: "2-digit", minute: "2-digit" });
    } catch {
        return "";
    }
};

export default function ChatRoom() {
    const { user } = useAuth();
    const [messages, setMessages] = useState([]);
    const [input, setInput] = useState("");
    const [sending, setSending] = useState(false);
    const [confirmDel, setConfirmDel] = useState(null);
    const scrollRef = useRef(null);
    const lastTs = useRef(null);
    const stick = useRef(true);

    const load = async (initial = false) => {
        try {
            const params = !initial && lastTs.current ? { after: lastTs.current } : {};
            const r = await api.get("/chatroom/messages", { params });
            if (!r.data.length) return;
            setMessages((m) => (initial ? r.data : [...m, ...r.data]));
            lastTs.current = r.data[r.data.length - 1].created_at;
        } catch { /* sessiz */ }
    };

    useEffect(() => {
        load(true);
        const t = setInterval(() => load(false), 4000);
        return () => clearInterval(t);
    }, []);

    useEffect(() => {
        if (stick.current) scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
    }, [messages]);

    const onScroll = () => {
        const el = scrollRef.current;
        if (!el) return;
        stick.current = el.scrollHeight - el.scrollTop - el.clientHeight < 80;
    };

    const send = async () => {
        const msg = input.trim();
        if (!msg || sending) return;
        setSending(true);
        try {
            const r = await api.post("/chatroom/messages", { content: msg });
            setMessages((m) => [...m, r.data]);
            lastTs.current = r.data.created_at;
            setInput("");
            stick.current = true;
        } catch (e) {
            toast.error(e.response?.data?.detail || "Mesaj gönderilemedi");
        } finally {
            setSending(false);
        }
    };

    const del = async () => {
        const id = confirmDel;
        setConfirmDel(null);
        try {
            await api.delete(`/chatroom/messages/${id}`);
            setMessages((m) => m.filter((x) => x.message_id !== id));
            toast.success("Mesaj silindi");
        } catch (e) {
            toast.error(e.response?.data?.detail || "Silinemedi");
        }
    };

    return (
        <div className="flex h-[calc(100dvh-8.5rem)] flex-col" data-testid="chatroom-page">
            <div className="flex items-center gap-3 pb-4">
                <MessagesSquare className="h-7 w-7 text-terra-600" />
                <div>
                    <h1 className="font-display text-3xl font-bold tracking-tight">Sohbet Odası</h1>
                    <p className="mt-1 text-sm text-muted-foreground">Tüm üyelerin ortak sohbet alanı — kayıtlı adınızla katılırsınız.</p>
                </div>
            </div>

            <div ref={scrollRef} onScroll={onScroll} className="flex-1 space-y-3 overflow-y-auto rounded-2xl border bg-card p-4 scrollbar-thin" data-testid="chatroom-messages">
                {messages.length === 0 && (
                    <div className="flex h-full flex-col items-center justify-center gap-2 text-center" data-testid="chatroom-empty">
                        <MessagesSquare className="h-10 w-10 text-muted-foreground/40" />
                        <p className="text-sm text-muted-foreground">Henüz mesaj yok — ilk mesajı siz gönderin.</p>
                    </div>
                )}
                {messages.map((m) => {
                    const own = m.user_id === user?.user_id;
                    return (
                        <div key={m.message_id} className={`group flex ${own ? "justify-end" : "justify-start"}`} data-testid={`chatroom-message-${m.message_id}`}>
                            <div className={`max-w-[80%] sm:max-w-[65%] ${own ? "text-right" : ""}`}>
                                {!own && (
                                    <p className="mb-1 flex items-center gap-2 text-xs">
                                        <span className="font-semibold">{m.user_name}</span>
                                        <span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold ${ROLE_CLS[m.role] || ROLE_CLS.buyer}`} data-testid={`chatroom-role-${m.message_id}`}>
                                            {ROLE_TR[m.role] || "Üye"}
                                        </span>
                                    </p>
                                )}
                                <div className="flex items-end gap-1.5">
                                    {!own && user?.role === "admin" && (
                                        <button
                                            onClick={() => setConfirmDel(m.message_id)}
                                            className="mb-1 rounded-md p-1 text-muted-foreground opacity-0 transition-opacity hover:text-destructive group-hover:opacity-100"
                                            title="Mesajı sil"
                                            data-testid={`chatroom-delete-${m.message_id}`}
                                        >
                                            <Trash2 className="h-3.5 w-3.5" />
                                        </button>
                                    )}
                                    <div
                                        className={`inline-block whitespace-pre-wrap rounded-2xl px-4 py-2.5 text-sm leading-relaxed ${
                                            own ? "bg-terra-600 text-white" : "border bg-background"
                                        }`}
                                    >
                                        {m.content}
                                    </div>
                                    {own && user?.role === "admin" && (
                                        <button
                                            onClick={() => setConfirmDel(m.message_id)}
                                            className="mb-1 rounded-md p-1 text-muted-foreground opacity-0 transition-opacity hover:text-destructive group-hover:opacity-100"
                                            title="Mesajı sil"
                                            data-testid={`chatroom-delete-${m.message_id}`}
                                        >
                                            <Trash2 className="h-3.5 w-3.5" />
                                        </button>
                                    )}
                                </div>
                                <p className="mt-1 text-[10px] text-muted-foreground">{fmtTime(m.created_at)}</p>
                            </div>
                        </div>
                    );
                })}
            </div>

            <form onSubmit={(e) => { e.preventDefault(); send(); }} className="flex gap-2 pt-3">
                <input
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                    placeholder="Mesajınızı yazın... (saygılı iletişim kuralları geçerlidir)"
                    maxLength={500}
                    disabled={sending}
                    className="h-11 flex-1 rounded-xl border bg-card px-4 text-sm focus:outline-none focus:ring-1 focus:ring-terra-500 disabled:opacity-50"
                    data-testid="chatroom-input"
                />
                <button
                    type="submit"
                    disabled={sending || !input.trim()}
                    className="flex h-11 w-11 items-center justify-center rounded-xl bg-terra-600 text-white transition-colors hover:bg-terra-700 disabled:opacity-50"
                    data-testid="chatroom-send"
                >
                    <Send className="h-4 w-4" />
                </button>
            </form>

            <ConfirmDialog
                open={!!confirmDel}
                onClose={() => setConfirmDel(null)}
                onConfirm={del}
                title="Mesaj silinsin mi?"
                description="Bu mesaj sohbet odasından kalıcı olarak kaldırılır."
                confirmText="Evet, Sil"
                destructive
                testId="chatroom-delete-dialog"
            />
        </div>
    );
}
